import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { ArrowRight, Apple, Dumbbell, HeartPulse, Sparkles } from 'lucide-react';
import { RegenLogo } from '@/components/RegenLogo';

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success('Welcome back');
      nav(user.role === 'admin' ? '/admin/dashboard' : '/app/home');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground" data-testid="login-page">
      {/* Brand panel — desktop only */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(140deg, #E6F4FB 0%, #EAF6FA 45%, #E3F7F0 100%)' }}>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(0,200,255,0.18), transparent 70%)' }} />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(184,255,0,0.12), transparent 70%)' }} />
        <RegenLogo size="md" withIcon className="relative z-10" />
        <div className="relative z-10 max-w-lg">
          <h2 className="font-display font-black text-5xl leading-[1.05] tracking-tight text-[#0A2E5C]">
            Transform your body.<br />Improve your health.<br /><span className="text-[#00B4C7]">Live longer.</span>
          </h2>
          <p className="text-[#3a4a5e] text-base mt-6 leading-relaxed max-w-md">
            Your personalised nutrition, training and recovery protocol — all in one place.
          </p>
          <div className="flex flex-wrap gap-3 mt-10">
            {[['Nutrition', Apple, '#FFB800'], ['Training', Dumbbell, '#00C8FF'], ['Recovery', HeartPulse, '#7DD3FC'], ['Longevity', Sparkles, '#65A30D']].map(([label, Icon, c]) => (
              <div key={label} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-white shadow-sm">
                <Icon className="w-4 h-4" style={{ color: c }} />
                <span className="text-sm font-semibold text-[#0A2E5C]">{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-[#5b6b7e]">© {new Date().getFullYear()} YourReGen. All rights reserved.</div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col justify-center px-6 py-14 sm:px-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden mb-8"><RegenLogo size="lg" withIcon /></div>
          <h1 className="font-display font-black text-4xl tracking-tight">Welcome back</h1>
          <p className="text-muted-foreground text-base mt-2">Sign in to your YourReGen account.</p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="mt-1.5 bg-card border-border h-12" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required className="mt-1.5 bg-card border-border h-12" />
              <div className="text-right mt-1.5">
                <button type="button" onClick={() => nav('/forgot-password')} data-testid="forgot-password-link" className="text-xs text-[#00B4C7] hover:underline">Forgot Password?</button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="h-14 w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl text-base">
              {loading ? 'Signing in…' : <>Sign In <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
            <div className="text-center text-sm text-muted-foreground pt-1">
              New here? <button type="button" onClick={() => { window.location.href = '/#plans'; }} data-testid="login-start-journey" className="text-[#00B4C7] font-semibold">Start the journey</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
